import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Wifi, WifiOff, RefreshCw } from 'lucide-react';
import { safeSentryCapture } from '@/utils/sentry';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onRetry?: () => void;
}

interface State {
  hasNetworkError: boolean;
  isOnline: boolean;
  retryCount: number;
}

export class NetworkErrorBoundary extends Component<Props, State> {
  private maxRetries = 3;
  
  public state: State = {
    hasNetworkError: false,
    isOnline: navigator.onLine,
    retryCount: 0,
  };

  public static getDerivedStateFromError(error: Error): Partial<State> {
    // Check if error is network-related
    const isNetworkError = error.message.includes('fetch') || 
                          error.message.includes('network') ||
                          error.message.includes('timeout') ||
                          error.message.includes('Failed to load');
    
    return { hasNetworkError: isNetworkError };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (this.state.hasNetworkError) {
      safeSentryCapture('captureException', error, {
        tags: {
          errorType: 'network',
          component: 'NetworkErrorBoundary'
        },
        extra: {
          errorInfo,
          isOnline: navigator.onLine,
          retryCount: this.state.retryCount
        }
      });
    }
  }

  componentDidMount() {
    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);
  }

  componentWillUnmount() {
    window.removeEventListener('online', this.handleOnline);
    window.removeEventListener('offline', this.handleOffline);
  }

  private handleOnline = () => {
    this.setState({ isOnline: true });
    // Auto-retry when connection is restored
    if (this.state.hasNetworkError && this.state.retryCount < this.maxRetries) {
      this.handleRetry();
    }
  };

  private handleOffline = () => {
    this.setState({ isOnline: false });
  };

  private handleRetry = () => {
    if (this.state.retryCount >= this.maxRetries) {
      return;
    }
    
    this.setState(prevState => ({ 
      hasNetworkError: false, 
      retryCount: prevState.retryCount + 1 
    }));
    
    this.props.onRetry?.();
  };

  private handleForceReload = () => {
    window.location.reload();
  };

  public render() {
    if (this.state.hasNetworkError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const canRetry = this.state.retryCount < this.maxRetries;

      return (
        <Card className="border-warning bg-warning/10">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-warning">
              {this.state.isOnline ? <Wifi className="h-5 w-5" /> : <WifiOff className="h-5 w-5" />}
              Network Connection Issue
            </CardTitle>
            <CardDescription>
              {!this.state.isOnline 
                ? "You're currently offline. Please check your internet connection."
                : "There was a problem loading content. This might be due to a temporary network issue."
              }
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-2">
              {canRetry && (
                <Button 
                  onClick={this.handleRetry} 
                  variant="outline" 
                  size="sm"
                  disabled={!this.state.isOnline}
                >
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Try Again ({this.maxRetries - this.state.retryCount} attempts left)
                </Button>
              )}
              <Button onClick={this.handleForceReload} size="sm">
                Reload Page
              </Button>
            </div>
            {!this.state.isOnline && (
              <p className="text-sm text-warning">
                Waiting for connection to be restored...
              </p>
            )}
          </CardContent>
        </Card>
      );
    }

    return this.props.children;
  }
}